<template>
  <div class="login-outer">
    <div class="login-card">
      <h2 class="welcome-title">管理后台</h2>
      <p class="welcome-subtitle">欢迎登录</p>

      <el-form
        ref="loginForm"
        :model="loginForm"
        :rules="loginRules"
        label-width="0px"
        class="login-form"
      >
        <el-form-item prop="username">
          <el-input
            v-model="loginForm.username"
            placeholder="请输入账号"
            prefix-icon="el-icon-user"
            clearable
            @keyup.enter.native="handleLogin"
          />
        </el-form-item>
        <el-form-item prop="password">
          <el-input
            v-model="loginForm.password"
            type="password"
            placeholder="请输入密码"
            prefix-icon="el-icon-lock"
            show-password
            @keyup.enter.native="handleLogin"
          />
        </el-form-item>

        <el-form-item class="login-btn-item">
          <el-button
            type="primary"
            class="login-btn"
            :loading="loading"
            @click="handleLogin"
          >
            立即登录
          </el-button>
        </el-form-item>

        <div class="form-extra">
          <el-checkbox v-model="rememberMe">记住我</el-checkbox>
        </div>
      </el-form>
    </div>
  </div>
</template>

<script>
/**
 * 解码 JWT token 的 payload 部分（不验证签名）
 * @param {string} token - JWT token 字符串
 * @returns {object|null} 解码后的 payload 对象
 */
function parseJwtPayload(token) {
  try {
    const parts = token.split(".");
    if (parts.length !== 3) return null;
    const base64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const json = decodeURIComponent(
      atob(base64)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join("")
    );
    return JSON.parse(json);
  } catch (e) {
    return null;
  }
}

export default {
  name: "LoginPage",
  data() {
    return {
      loginForm: {
        username: "",
        password: "",
      },
      loginRules: {
        username: [
          { required: true, message: "请输入用户名", trigger: "blur" },
        ],
        password: [{ required: true, message: "请输入密码", trigger: "blur" }],
      },
      loading: false,
      rememberMe: true,
    };
  },
  methods: {
    async handleLogin() {
      if (!this.rememberMe) {
        this.$message.warning("请勾选「记住我」后再登录");
        return;
      }
      this.$refs.loginForm.validate(async (valid) => {
        if (valid) {
          this.loading = true;
          try {
            const response = await fetch("/admin/login", {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify({
                username: this.loginForm.username,
                password: this.loginForm.password,
              }),
            });

            const result = await response.json();

            if (result.code === 200 && result.data && result.data.token) {
              const token = result.data.token;
              localStorage.setItem("token", token);

              const payload = parseJwtPayload(token);
              if (!payload) {
                localStorage.removeItem("token");
                this.$message.error("解析登录信息失败");
                this.loading = false;
                return;
              }

              if (payload.status === 1) {
                localStorage.removeItem("token");
                this.$message.error("该账号已被禁用，请联系管理员");
                this.loading = false;
                return;
              }

              const roleVal = parseInt(payload.role, 10);
              const statusVal = parseInt(payload.status, 10);

              if (roleVal !== 0 && roleVal !== 1) {
                localStorage.removeItem("token");
                this.$message.error("账号角色无效，请联系管理员");
                this.loading = false;
                return;
              }

              localStorage.setItem("role", roleVal);
              localStorage.setItem("status", !isNaN(statusVal) ? statusVal : 0);
              localStorage.setItem(
                "username",
                payload.username || this.loginForm.username
              );
              const userId =
                payload.adminid ||
                payload.adminId ||
                result.data.id ||
                result.data.userId ||
                payload.id ||
                payload.userId ||
                payload.sub ||
                "";
              localStorage.setItem("userId", userId);

              if (this.rememberMe) {
                localStorage.setItem(
                  "rememberedUsername",
                  this.loginForm.username
                );
              } else {
                localStorage.removeItem("rememberedUsername");
              }

              this.$router.push("/home");
              this.$message.success("登录成功");
            } else {
              this.$message.error(result.msg || "登录失败：未返回有效数据");
            }
          } catch (error) {
            this.$message.error(error.message || "登录失败");
          } finally {
            this.loading = false;
          }
        }
      });
    },
  },
  mounted() {
    const rememberedUsername = localStorage.getItem("rememberedUsername");
    if (rememberedUsername) {
      this.loginForm.username = rememberedUsername;
      this.rememberMe = true;
    }
  },
};
</script>

<style scoped>
.login-outer {
  --login-blue: #4e7cff;
  --login-blue-dark: #3d6aef;
  width: 100vw;
  height: 100vh;
  overflow: hidden;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding: clamp(24px, 5vh, 48px) clamp(40px, 10vw, 140px);
  background-color: #f0f4f8;
  background-image: url("../assets/login-bg.webp");
  background-repeat: no-repeat;
  background-position: left center;
  background-size: cover;
}

.login-card {
  width: 100%;
  max-width: 420px;
  padding: clamp(36px, 5vh, 52px) clamp(32px, 4vw, 48px) clamp(28px, 4vh, 40px);
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 12px 40px rgba(55, 85, 140, 0.12);
  box-sizing: border-box;
}

.welcome-title {
  margin: 0 0 10px;
  text-align: center;
  font-size: clamp(24px, 2.2vw, 30px);
  font-weight: 700;
  color: #1f2937;
  letter-spacing: 1px;
  line-height: 1.3;
}

.welcome-subtitle {
  margin: 40px 0 clamp(12px, 2vh, 20px);
  text-align: left;
  font-size: clamp(18px, 1.5vw, 22px);
  font-weight: 500;
  color: #374151;
  line-height: 1.4;
}

.login-form >>> .el-form-item {
  margin-bottom: clamp(16px, 2.2vh, 22px);
}

.login-form >>> .el-input__inner {
  height: 48px;
  line-height: 48px;
  border-radius: 10px;
  border: 1px solid #e5e7eb;
  background: #fff;
  padding-left: 42px;
  font-size: 15px;
  color: #374151;
  transition: border-color 0.2s;
}

.login-form >>> .el-input__inner:focus {
  border-color: var(--login-blue);
}

.login-form >>> .el-input__inner::placeholder {
  color: #9ca3af;
}

.login-form >>> .el-input__prefix {
  left: 12px;
}

.login-form >>> .el-input__prefix .el-input__icon {
  line-height: 48px;
  width: 20px;
  color: #9ca3af;
  font-size: 16px;
}

.login-form >>> .el-input__suffix {
  right: 12px;
}

.login-form >>> .el-input__clear,
.login-form >>> .el-input__password {
  color: #9ca3af;
}

.login-btn-item {
  margin-bottom: 16px !important;
  margin-top: 6px;
}

.login-btn {
  width: 100%;
  height: 48px;
  border-radius: 10px !important;
  font-size: 16px;
  font-weight: 600;
  letter-spacing: 2px;
  background: var(--login-blue) !important;
  border: none !important;
  box-shadow: 0 6px 16px rgba(78, 124, 255, 0.3);
  transition: background 0.2s, box-shadow 0.2s, transform 0.15s;
}

.login-btn:hover,
.login-btn:focus {
  background: var(--login-blue-dark) !important;
  box-shadow: 0 8px 20px rgba(61, 106, 239, 0.38) !important;
  transform: translateY(-1px);
}

.form-extra {
  display: flex;
  align-items: center;
  padding: 0 2px;
}

.form-extra >>> .el-checkbox__label {
  font-size: 14px;
  color: #6b7280;
  padding-left: 6px;
}

.form-extra >>> .el-checkbox__input.is-checked .el-checkbox__inner {
  background-color: var(--login-blue);
  border-color: var(--login-blue);
}

.form-extra >>> .el-checkbox__inner:hover {
  border-color: var(--login-blue);
}

@media screen and (max-width: 900px) {
  .login-outer {
    justify-content: center;
    padding: 24px 20px;
    background-position: center left;
    background-size: cover;
  }

  .login-card {
    max-width: 400px;
  }
}

@media screen and (max-width: 480px) {
  .login-outer {
    padding: 16px;
    align-items: center;
  }

  .login-card {
    max-width: 100%;
    padding: 32px 24px 28px;
  }

  .welcome-title {
    font-size: 22px;
  }
}
</style>
